/**
 * The CreditCardApprovalResponse component displays information related to credit card approval, such as credit limits and APR values.
 *
 * @module views/components/CreditCardApprovalResponse
 * @memberof -Common
 */
import './CreditCardApprovalResponse.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import MetadataList from '@ulta/modules/MetadataList/MetadataList';
import PromotedCardMini from '@ulta/modules/PromotedCardMini/PromotedCardMini';
import Section from '@ulta/modules/Section/Section';

import { formatCurrency } from '@ulta/utils/Formatters/Formatters';

/**
 * Represents a CreditCardApprovalResponse component
 *
 * @method
 * @param {CreditCardApprovalResponseProps} props - React properties passed from composition
 * @returns CreditCardApprovalResponse
 */
export const CreditCardApprovalResponse = props => {

  const {
    aprLabel,
    aprInfoMessage,
    aprSuperscriptValue,
    aprValue,
    creditLimit,
    creditLimitLabel,
    componentKey,
    discountMessage,
    transactionLimit,
    transactionLimitLabel,
    rewardsMessage,
    viewDetailsAction
  } = props;

  const personalDetailsMetadata = {
    [creditLimitLabel]: formatCurrency( creditLimit ),
    [aprLabel]: aprValue && (
      <div>
        { aprSuperscriptValue && (
          <>
            { `${aprValue}%` }
            <sup>{ aprSuperscriptValue }</sup>
          </>
        ) }
        { !aprSuperscriptValue && `${aprValue}%` }
      </div>
    ),
    [transactionLimitLabel]: formatCurrency( transactionLimit )
  };

  return (
    <div className='CreditCardApprovalResponse'>
      <MetadataList
        containerClassName='CreditCardApprovalResponse__MetadataListContainer'
        componentKey={ `MetadataList-${ componentKey }` }
        data={ Object.entries( personalDetailsMetadata ) }
        labelColor='neutral-600'
        labelTextStyle='eyebrow'
        stacked
        title={ 'Credit Approval Response Details' }
        valueColor='black'
        valueTextStyle='title-5'
      />
      { aprInfoMessage && (
        <div className='CreditCardApprovalResponse__AprMessageContainer'>
          <Section { ...spreadProps( { ...aprInfoMessage } ) } />
          { viewDetailsAction?.label && (
            <Button
              label={ viewDetailsAction.label }
              likeLink
              action={ viewDetailsAction }
            />
          ) }
        </div>
      ) }
      { discountMessage && <Section { ...spreadProps( { ...discountMessage } ) } /> }
      { rewardsMessage &&
      <PromotedCardMini
        { ...spreadProps( { ...rewardsMessage } ) }
        displayAsMobile={ true }
      /> }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef CreditCardApprovalResponseProps
 * @type {object}
 * @property {string} creditLimitLabel - The label for the credit limit.
 * @property {string} creditLimit - The actual credit limit value.
 * @property {string} aprLabel - The label for the APR.
 * @property {object} aprInfoMessage - aprInfoMessage Section
 * @property {string} aprValue - The actual APR value.
 * @property {string} aprSuperscriptValue - The superscript value for the APR.
 * @property {string} transactionLimitLabel - The label for the transaction limit.
 * @property {string} transactionLimit - The actual transaction limit value.
 * @property {object} discountMessage - discountMessage Section
 * @property {object} rewardsMessage - rewardsMessage Section
 * @property {object} viewDetailsAction - viewDetailsAction
 */

export const propTypes = {
  creditLimitLabel: PropTypes.string,
  creditLimit: PropTypes.number,
  aprLabel: PropTypes.string,
  aprValue: PropTypes.number,
  aprInfoMessage: PropTypes.object,
  aprSuperscriptValue: PropTypes.string,
  transactionLimitLabel: PropTypes.string,
  transactionLimit: PropTypes.number,
  discountMessage: PropTypes.object,
  rewardsMessage: PropTypes.object,
  viewDetailsAction: PropTypes.object
};

export default CreditCardApprovalResponse;
